import { useCallback, useMemo, useState } from 'react';
import * as ReactDOM from 'react-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { $createTextNode } from 'lexical';
import { $createMentionNode } from '../nodes';
import { useMentionCandidatesContext } from '../mention-context';
import type { MentionCandidate } from '../mention-types';

/** A single typeahead row. Lexical's MenuOption gives us focus/selection
 *  state plumbing for free; we just tack the candidate onto it. */
class MentionTypeaheadOption extends MenuOption {
  constructor(public readonly candidate: MentionCandidate) {
    super(candidate.id);
  }
}

const MAX_RESULTS = 10;

/** Score-and-filter candidates against the typed query.
 *  Case-insensitive `includes` on display name, plus phone-prefix on the
 *  JID for users who start typing digits. Exact / startsWith matches are
 *  bubbled to the top so the common "type a couple of letters and Enter"
 *  flow lands on the right person.
 */
function filterCandidates(
  candidates: MentionCandidate[],
  query: string | null,
): MentionCandidate[] {
  if (!query) return candidates.slice(0, MAX_RESULTS);
  const q = query.toLowerCase();
  const ranked = candidates
    .map((c) => {
      const name = c.display.toLowerCase();
      const phone = c.id.split('@')[0];
      let rank = -1;
      if (name === q) rank = 0;
      else if (name.startsWith(q)) rank = 1;
      else if (name.includes(' ' + q)) rank = 2;
      else if (name.includes(q)) rank = 3;
      else if (phone.startsWith(q)) rank = 4;
      return { c, rank };
    })
    .filter(({ rank }) => rank >= 0)
    .sort((a, b) => a.rank - b.rank);
  return ranked.slice(0, MAX_RESULTS).map((r) => r.c);
}

export function MentionPlugin(): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const [query, setQuery] = useState<string | null>(null);
  const { candidates, isLoading } = useMentionCandidatesContext();

  // Standard "@..." trigger with no min-length, so the menu pops the
  // moment the user types "@". Punctuation default is fine.
  const triggerFn = useBasicTypeaheadTriggerMatch('@', { minLength: 0 });

  const filtered = useMemo(() => filterCandidates(candidates, query), [candidates, query]);

  const options = useMemo(
    () => filtered.map((c) => new MentionTypeaheadOption(c)),
    [filtered],
  );

  const onSelectOption = useCallback(
    (
      selectedOption: MentionTypeaheadOption,
      nodeToReplace: any | null,
      closeMenu: () => void,
    ) => {
      console.log('[mention-debug] onSelectOption fired', {
        candidate: selectedOption.candidate,
        hasNodeToReplace: !!nodeToReplace,
      });
      editor.update(() => {
        const mention = $createMentionNode({
          id: selectedOption.candidate.id,
          display: selectedOption.candidate.display,
        });
        // The plugin gives us the TextNode that contains the "@query"
        // typed so far. Replace it with our chip; if for some reason
        // it's null, the plugin's own cleanup will handle removal of
        // the query text via the editor selection.
        if (nodeToReplace) {
          nodeToReplace.replace(mention);
        }
        // Trailing space so the caret moves past the chip and the user
        // can keep typing immediately. Matches WA's own behaviour.
        const space = $createTextNode(' ');
        mention.insertAfter(space);
        space.select();
        closeMenu();
      });
    },
    [editor],
  );

  return (
    <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
      onQueryChange={setQuery}
      onSelectOption={onSelectOption}
      triggerFn={triggerFn}
      options={options}
      // Highlight the first row by default so pressing Enter immediately
      // picks it — without this, `selectedIndex` starts as null and Enter
      // does nothing until the user presses ArrowDown first.
      preselectFirstItem
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex },
      ) => {
        if (anchorElementRef.current === null) return null;

        // Empty state: still render the panel so the user gets feedback.
        // - No recipients selected → hint to pick one first.
        // - Recipients selected but candidates not yet loaded → spinner.
        // - Filter has no hits → "no matches".
        const showEmpty = options.length === 0;
        const emptyMsg = showEmpty
          ? isLoading
            ? 'Loading…'
            : candidates.length === 0
              ? 'Select a recipient to mention people'
              : 'No matches'
          : null;

        return ReactDOM.createPortal(
          <div
            className="bt-mention-menu"
            data-mention-menu="true"
            // WhatsApp Web installs a capture-phase wheel listener on its
            // chat panel that swallows scroll events for anything injected
            // on top of it. Without these handlers the menu's own scroll
            // container never sees the wheel deltas, and dragging the
            // scrollbar feels frozen. We stop propagation BEFORE WA can
            // catch the event; overscroll-behavior keeps the scroll
            // contained so reaching the end doesn't chain to the page.
            onWheel={(e) => {
              e.stopPropagation();
            }}
            onTouchMove={(e) => {
              e.stopPropagation();
            }}
            // NOTE: we used to stopPropagation on pointerdown too, but on
            // the website's Dialog component that swallowed clicks on the
            // <li> rows entirely. The wheel handler above is enough to
            // keep WA from stealing scroll.
          >
            <ul role="listbox">
              {options.map((option, i) => (
                <li
                  key={option.candidate.id}
                  role="option"
                  aria-selected={i === selectedIndex}
                  className={i === selectedIndex ? 'bt-mention-row active' : 'bt-mention-row'}
                  ref={option.setRefElement}
                  onMouseEnter={() => setHighlightedIndex(i)}
                  onMouseDown={(e) => {
                    // preventDefault on mousedown stops the editor from
                    // blurring before our click registers — without
                    // this, the next editor.update() call would land
                    // with no selection and silently no-op.
                    e.preventDefault();
                    e.stopPropagation();
                    setHighlightedIndex(i);
                    console.log('[mention-debug] mousedown selecting', option.candidate);
                    selectOptionAndCleanUp(option);
                  }}
                  // onClick is a belt-and-suspenders for cases where
                  // some parent component swallows mousedown (e.g.
                  // some modal libraries pre-empt mousedown on overlay).
                  // Re-running the selection is a cheap no-op if the
                  // mousedown handler already handled it (the menu is
                  // already closed; selectOptionAndCleanUp is idempotent
                  // against the now-removed node).
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('[mention-debug] click fallback', option.candidate);
                    setHighlightedIndex(i);
                    selectOptionAndCleanUp(option);
                  }}
                >
                  <span className="bt-mention-row__name">
                    {option.candidate.display}
                  </span>
                  <span className="bt-mention-row__id">
                    {option.candidate.id.split('@')[0]}
                  </span>
                </li>
              ))}
              {emptyMsg && (
                <li className="bt-mention-row empty">{emptyMsg}</li>
              )}
            </ul>
            <style>{`
              .bt-mention-menu {
                position: absolute;
                top: 100%;
                inset-inline-start: 0;
                margin-top: 4px;
                min-width: 260px;
                max-width: 360px;
                max-height: 280px;
                overflow-y: auto;
                /* Stops scroll-chaining: hitting the top/bottom of the
                   list doesn't start scrolling the page behind. Pairs
                   with the stopPropagation wheel handler above. */
                overscroll-behavior: contain;
                /* Solid neutral background that works on both light and
                   dark hosts. We DON'T use var(--color-surface) because
                   that resolves to dark in some contexts and to white in
                   others — and on a light page the dark fallback made
                   the menu fight the page. */
                background: #ffffff;
                color: #111b21;
                border: 1px solid rgba(0, 0, 0, 0.12);
                border-radius: 10px;
                box-shadow: 0 16px 40px rgba(0, 0, 0, 0.22),
                            0 2px 6px rgba(0, 0, 0, 0.08);
                /* Highest practical z-index; sits above MUI Dialog
                   (1300), Radix Dialog (~999), and WA Web overlays. */
                z-index: 2147483647;
                /* Ensure pointer events reach our list rows even if a
                   parent has set pointer-events: none somewhere. */
                pointer-events: auto;
                font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
              }
              @media (prefers-color-scheme: dark) {
                .bt-mention-menu {
                  background: #1f2c33;
                  color: #e9edef;
                  border-color: rgba(255, 255, 255, 0.12);
                  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5),
                              0 2px 6px rgba(0, 0, 0, 0.25);
                }
              }
              .bt-mention-menu::-webkit-scrollbar {
                width: 8px;
              }
              .bt-mention-menu::-webkit-scrollbar-thumb {
                background: rgba(0, 0, 0, 0.18);
                border-radius: 4px;
              }
              .bt-mention-menu::-webkit-scrollbar-thumb:hover {
                background: rgba(0, 0, 0, 0.32);
              }
              .bt-mention-menu::-webkit-scrollbar-track {
                background: transparent;
              }
              .bt-mention-menu ul {
                list-style: none;
                margin: 0;
                padding: 4px 0;
              }
              .bt-mention-row {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 10px;
                padding: 9px 12px;
                cursor: pointer;
                font-size: 14px;
                line-height: 1.3;
                user-select: none;
                /* Forces a stacking context so the hover/active bgs
                   never get hidden by sibling backgrounds. */
                position: relative;
              }
              /* Mouse-hover highlight — visible on white *and* dark.
                 Distinct from .active so the user can tell what's under
                 the cursor vs what's keyboard-selected. */
              .bt-mention-row:hover {
                background: rgba(0, 0, 0, 0.06);
              }
              @media (prefers-color-scheme: dark) {
                .bt-mention-row:hover {
                  background: rgba(255, 255, 255, 0.08);
                }
              }
              /* Keyboard-selected row (arrow keys) — accent tint, wins
                 over plain hover. */
              .bt-mention-row.active,
              .bt-mention-row.active:hover {
                background: rgba(0, 168, 132, 0.14);
              }
              .bt-mention-row.empty,
              .bt-mention-row.empty:hover {
                color: #667781;
                cursor: default;
                font-style: italic;
                font-size: 13px;
                background: transparent;
              }
              .bt-mention-row__name {
                font-weight: 500;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                max-width: 200px;
              }
              .bt-mention-row__id {
                color: #667781;
                font-size: 12px;
                font-variant-numeric: tabular-nums;
              }
              /* The chip inside the editor itself. */
              .editor-mention-chip {
                display: inline-block;
                padding: 0 4px;
                border-radius: 4px;
                background: rgba(0, 168, 132, 0.18);
                color: #00a884;
                font-weight: 500;
                white-space: nowrap;
              }
            `}</style>
          </div>,
          anchorElementRef.current,
        );
      }}
    />
  );
}
